import { CardPreview } from "@bokio/components/CardPreview/CardPreview";
import { mergeClassNames } from "@bokio/utils/classes";

import * as styles from "./feedback.scss";

export const FeedbackGraphicTypes = [
	"agencyClientComments",
	"agencyClientsList",
	"agencyClientsRequests",
	"agencyClientTasks",
	"agencyDashboardComments",
	"agencyDashboardTasks",
	"bankInboxMessage",
	"cardPreviewMini",
	"closureJournals",
	"customTodoEmptyState",
	"emptyState",
	"emptyStateAccountantPanel",
	"emptyStateAlt1",
	"emptyStateAlt2",
	"emptyStateAlt3",
	"etrEmptyStateConnectBank",
	"etrEmptyStateNoEvents",
	"expenseClaimsPayments",
	"expenseClaimsPermissions",
	"expensesAllExpensesClailms",
	"expensesMyExpensesClaims",
	"externalOnboarding",
	"factoringOnboarding",
	"noBookkeeping",
	"noExpenses",
	"noNotifications",
	"payrollTaxes",
	"purchasesSuppliers",
	"settingsBankRules",
	"settingsCustomBookkeeping",
	"settingsImportPreviousBookkeeping",
	"staffSalaryEmployees",
	"staffSalaryVacation",
	"supplierInvoices",
	"todoEmptyState",
	"UploadsAdditionalFiles",
	"UploadsReceiptsOrInvoices",
	"vacation",
	// Reserved for brand lift migration
	"Debbie_hi",
	"Debbie_with_key",
	"Debbie_calendar",
	"Debbie_hi_with_a_lightface",
	"Debbie_looking_for_information",
	"Debbie_no_expenses",
	"Debbie_todo",
	"Debbie_cheering_with_pom_poms",
	"Debbie_waiting_in_the_clock",
	"Debbie_binoculars",
] as const;

export type FeedbackGraphicType = (typeof FeedbackGraphicTypes)[number];

interface FeedbackGraphicProps {
	type: FeedbackGraphicType;
	testId?: string;
	className?: string;
	/**
	 * @deprecated To be cleaned up along with EmptyState component
	 */
	dontUse_targetWidth?: false | number | string;
}

export const FeedbackGraphic = ({ type, testId, className, dontUse_targetWidth }: FeedbackGraphicProps) => {
	const getClassName = (type: FeedbackGraphicType): string => {
		switch (type) {
			case "closureJournals":
				return styles.Debbie_journals;
			case "settingsImportPreviousBookkeeping":
				return styles.Debbie_upload;
			case "settingsCustomBookkeeping":
				return styles.Debbie_write;
			case "settingsBankRules":
				return styles.SettingsBankRules;
			case "staffSalaryEmployees":
				return styles.Debbie_looking_for_information;
			case "staffSalaryVacation":
				return styles.Debbie_smile_and_wave;
			case "expensesAllExpensesClailms":
				return styles.Debbie_expenses;
			case "expenseClaimsPermissions":
				return styles.Debbie_expenses;
			case "expensesMyExpensesClaims":
				return styles.Debbie_expenses;
			case "noExpenses":
				return styles.Debbie_no_expenses;
			case "expenseClaimsPayments":
				return styles.Debbie_expenses;
			case "purchasesSuppliers":
				return styles.Debbie_hi;
			case "UploadsReceiptsOrInvoices":
				return styles.Debbie_checks_receipt;
			case "noBookkeeping":
				return styles.Debbie_search_information;
			case "UploadsAdditionalFiles":
				return styles.Debbie_with_files;
			case "externalOnboarding":
				return styles.imageExternalOnboarding;
			case "factoringOnboarding":
				return styles.imageFactoringOnboarding;
			case "noNotifications":
				return styles.Debbie_chilling_with_coffee;
			case "payrollTaxes":
				return styles.imagePayrollTaxes;
			case "vacation":
				return styles.imageVacation;
			case "emptyState":
				return styles.Debbie_calm;
			case "emptyStateAlt1":
				return styles.Debbie_edits;
			case "emptyStateAlt2":
				return styles.Debbie_hi;
			case "emptyStateAlt3":
				return styles.Debbie_calm;
			case "agencyClientsList":
				return styles.Debbie_looking_for_clients;
			case "agencyClientsRequests":
				return styles.Debbie_enjoying_the_coffee;
			case "agencyClientComments":
				return styles.Debbie_comment;
			case "agencyClientTasks":
				return styles.Debbie_checklist;
			case "agencyDashboardComments":
				return styles.Debbie_comment;
			case "agencyDashboardTasks":
				return styles.Debbie_checklist;
			case "bankInboxMessage":
				return styles.Debbie_inbox;
			case "supplierInvoices":
				return styles.Debbie_hi;
			case "emptyStateAccountantPanel":
				return styles.imageEmptyStateAccountantPanel;
			case "customTodoEmptyState":
				return styles.Debbie_todo;
			case "todoEmptyState":
				return styles.Debbie_todo;
			case "etrEmptyStateConnectBank":
				return styles.Debbie_teaching;
			case "etrEmptyStateNoEvents":
				return styles.Debbie_success;
			case "cardPreviewMini":
				return ""; // The image is mounted from the CardPreview component instead.
			case "Debbie_hi":
				return styles.Debbie_hi;
			case "Debbie_with_key":
				return styles.Debbie_with_key;
			case "Debbie_calendar":
				return styles.Debbie_calendar;
			case "Debbie_hi_with_a_lightface":
				return styles.Debbie_hi_with_a_lightface;
			case "Debbie_looking_for_information":
				return styles.Debbie_looking_for_information;
			case "Debbie_no_expenses":
				return styles.Debbie_no_expenses;
			case "Debbie_todo":
				return styles.Debbie_todo;
			case "Debbie_cheering_with_pom_poms":
				return styles.Debbie_cheering_with_pom_poms;
			case "Debbie_waiting_in_the_clock":
				return styles.Debbie_waiting_in_the_clock;
			case "Debbie_binoculars":
				return styles.Debbie_binoculars;
		}
	};

	const classNames = mergeClassNames(styles.image, getClassName(type), className);

	return (
		<div
			data-testid={testId}
			className={classNames}
			style={
				dontUse_targetWidth
					? {
							"--feedback-graphic-target-width": `${typeof dontUse_targetWidth === "string" ? dontUse_targetWidth : `${dontUse_targetWidth}px`}`,
						}
					: undefined
			}
		>
			{type === "cardPreviewMini" && <CardPreview mini />}
		</div>
	);
};
